import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ISection, loadBalancerAPI } from '../../api/load-balancer';
import { useLocation } from 'react-router-dom';
import MarkdownRenderer from './MarkdownRenderer';
import './QuickstartStyle.css';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { authState } from '../../api';

interface OSLogoProps {
  section: ISection;
  sx?: object;
}

export const OSLogo: React.FC<OSLogoProps> = ({ section, sx }) => {
  const authStateValue = useRecoilValue(authState);
  const { i18n } = useTranslation();
  let languageCode: string;
  if (authStateValue?.user?.languageCode) {
    languageCode = authStateValue.user.languageCode;
  } else {
    languageCode = i18n.language;
  }
  return (
    <Stack
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        gap: '5px',
        ...sx,
      }}
    >
      <Box component="img" src={`quickstart/${languageCode}/images/${section.icon}`} sx={{ height: 28, width: 28 }} />
      <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.3rem' }}>
        {section.sectionName}
      </Typography>
    </Stack>
  );
};

const QuickStartGuide = () => {
  const { t, i18n } = useTranslation();
  const authStateValue = useRecoilValue(authState);
  const location = useLocation();
  const [filteredData, setFilteredData] = useState<ISection[]>([]);
  let languageCode: string;
  if (authStateValue?.user?.languageCode) {
    languageCode = authStateValue.user.languageCode;
  } else {
    languageCode = i18n.language;
  }

  const {
    data: allSectionData,
    isLoading,
    error,
  } = loadBalancerAPI.hookWrapperQuery.useGetAllSection({ enabled: true }, languageCode);

  const initialExpandedState = allSectionData
    ? Object.fromEntries(
        allSectionData.map((section) => [`${section.hashHyperLink}`, location.hash === `${section.hashHyperLink}`])
      )
    : {};
  const [expandedState, setExpandedState] = useState<{ [key: string]: boolean }>(initialExpandedState);
  const [markdownContents, setMarkdownContents] = useState<{ [key: string]: string }>({});
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});

  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (allSectionData) {
      const filteredSections = allSectionData.filter((section) => section.isPublish === true);
      setFilteredData(filteredSections);
      if (location.hash) {
        const sectionHash = location.hash;
        const sectionToExpand = `${sectionHash}`;
        setExpandedState((prevState) => ({
          ...prevState,
          [sectionToExpand]: true,
        }));
        const contentFile = filteredSections.find((section) => section.hashHyperLink === sectionHash)?.contentFile;
        if (contentFile) {
          fetchMarkdownContent(contentFile, sectionToExpand);
        }
      }
    }
  }, [allSectionData, location.hash]);

  useEffect(() => {
    if (location.hash && sectionRefs.current[location.hash]) {
      sectionRefs.current[location.hash]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash, sectionRefs]);

  const handleSectionExpand = (hash: string, contentFile: string) => {
    if (!expandedState[hash]) {
      setExpandedState((prevState) => ({
        ...prevState,
        [hash]: true,
      }));
      fetchMarkdownContent(contentFile, hash);
    } else {
      setExpandedState((prevState) => ({
        ...prevState,
        [hash]: false,
      }));
    }
  };

  const fetchMarkdownContent = async (contentFile: string, hash: string) => {
    if (markdownContents[hash]) return;
    setLoadingStates((prevState) => ({
      ...prevState,
      [hash]: true,
    }));
    try {
      const response = await fetch(`/quickstart/${languageCode}/md/${contentFile}`, {
        headers: {
          'Cache-Control': 'no-cache',
        },
      });
      if (!response.ok) {
        throw new Error(t('quickstartGuidePage.networkResponseNotOk'));
      }
      const text = await response.text();
      setMarkdownContents((prevContents) => ({
        ...prevContents,
        [hash]: text,
      }));
    } catch (error) {
      console.error(t('quickstartGuidePage.faiToFetchMarkdownContent'), error);
    } finally {
      setLoadingStates((prevState) => ({
        ...prevState,
        [hash]: false,
      }));
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (error) {
    return (
      <div>
        <Typography variant="h6" color="error">
          {t('quickstartGuidePage.failedToLoadSections')}
        </Typography>
      </div>
    );
  }

  const guideSections = filteredData || [];

  return (
    <div>
      <Card>
        <CardHeader title={t('quickstartGuidePage.quickstartGuide')} />
      </Card>
      {guideSections.map((section) => (
        <Accordion
          key={section.id}
          ref={(el) => (sectionRefs.current[`${section.hashHyperLink}`] = el)}
          expanded={expandedState[`${section.hashHyperLink}`]}
          onChange={() => handleSectionExpand(`${section.hashHyperLink}`, section.contentFile)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <OSLogo sx={{ flexDirection: 'row', gap: '10px' }} section={section} />
          </AccordionSummary>
          <AccordionDetails>
            {loadingStates[`${section.hashHyperLink}`] ? (
              <CircularProgress />
            ) : (
              <MarkdownRenderer markdown={markdownContents[`${section.hashHyperLink}`] || ''} />
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};

export default QuickStartGuide;
